import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import FormInput from '../../Components/Ui/FormInput';
import ButtonField from '../../Components/Ui/ButtonField';
import RightContentLogin from '../../Components/Auth/RightContentLogin';
import { LayoutAuth } from '../../Components/Auth/LayoutAuth';
import validatePassword from '../../Validation/Password';
import {
  FirebaseAuthServiceErrorCode,
  applyEmailActionCode,
  confirmPasswordResetLink,
  verifyPasswordResetLink,
} from '../../Services/FirebaseService';
import { ENV_DECLARONS, getActiveAppBrandConfig, isBrandActive } from '../../config/environment.config';

/** Longueur minimale imposée par Firebase. */
const MIN_PASSWORD_LENGTH = 6;

type Mode = 'resetPassword' | 'verifyEmail' | 'recoverEmail';
type Status = 'checking' | 'form' | 'working' | 'done' | 'invalid';

/**
 * Page d'action des e-mails Firebase (« Personnaliser l'URL d'action » dans
 * les modèles) : le lien reçu par mail arrive ici au lieu de la page neutre
 * de Firebase. Choix du mot de passe (mot de passe oublié et première
 * connexion), vérification et rétablissement d'adresse e-mail, aux couleurs
 * de la marque, puis retour à la connexion.
 */
function AuthActionPage() {
  const { classes, common, brandConfig } = useTailwindBrand();
  const primary = brandConfig.colors?.primary ?? '#0054A3';
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get('mode') as Mode | null;
  const oobCode = params.get('oobCode') ?? '';
  const appName = getActiveAppBrandConfig();

  const [status, setStatus] = useState<Status>('checking');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState('');
  const [cpassword, setCpassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState<string>('');
  const [invalidReason, setInvalidReason] = useState<string>('');

  const describeLinkError = (code?: string) => {
    if (code === FirebaseAuthServiceErrorCode.EXPIRED_ACTION_CODE) {
      return 'Ce lien a expiré ou a déjà été utilisé.';
    }
    if (code === FirebaseAuthServiceErrorCode.USER_DISABLED) {
      return 'Ce compte est désactivé. Contactez-nous pour le réactiver.';
    }
    if (code === FirebaseAuthServiceErrorCode.USER_NOT_FOUND) {
      return "Aucun compte ne correspond à ce lien.";
    }
    return "Ce lien n'est pas valide.";
  };

  // Vérification du code à l'arrivée : un lien de mot de passe ouvre le
  // formulaire, un lien d'e-mail s'applique directement.
  useEffect(() => {
    let cancelled = false;
    if (!oobCode || !mode) {
      setInvalidReason("Ce lien est incomplet.");
      setStatus('invalid');
      return undefined;
    }
    if (mode === 'resetPassword') {
      verifyPasswordResetLink(oobCode)
        .then((mail) => {
          if (cancelled) return;
          setEmail(mail);
          setStatus('form');
        })
        .catch((e: any) => {
          if (cancelled) return;
          setInvalidReason(describeLinkError(e?.code));
          setStatus('invalid');
        });
    } else if (mode === 'verifyEmail' || mode === 'recoverEmail') {
      applyEmailActionCode(oobCode)
        .then(() => {
          if (!cancelled) setStatus('done');
        })
        .catch((e: any) => {
          if (cancelled) return;
          setInvalidReason(describeLinkError(e?.code));
          setStatus('invalid');
        });
    } else {
      setInvalidReason("Ce type de lien n'est pas pris en charge.");
      setStatus('invalid');
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, oobCode]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const check = validatePassword(password, cpassword);
    if (!check.isValid) {
      setError(check.message);
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Votre mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`);
      return;
    }
    setError('');
    setStatus('working');
    try {
      await confirmPasswordResetLink(oobCode, password);
      setStatus('done');
    } catch (err: any) {
      if (err?.code === FirebaseAuthServiceErrorCode.WEAK_PASSWORD) {
        setError('Ce mot de passe est trop simple. Choisissez-en un plus long.');
        setStatus('form');
        return;
      }
      setInvalidReason(describeLinkError(err?.code));
      setStatus('invalid');
    }
  };

  const titles: Record<Mode, { header: string; done: string; doneText: string }> = {
    resetPassword: {
      header: 'Choisissez votre mot de passe',
      done: 'Votre mot de passe est enregistré',
      doneText: `Vous pouvez maintenant vous connecter à votre espace ${appName} avec votre adresse e-mail et ce mot de passe.`,
    },
    verifyEmail: {
      header: 'Vérification de votre adresse e-mail',
      done: 'Adresse e-mail vérifiée',
      doneText: `Merci, votre adresse est confirmée. Vous pouvez vous connecter à votre espace ${appName}.`,
    },
    recoverEmail: {
      header: 'Rétablissement de votre adresse e-mail',
      done: 'Adresse e-mail rétablie',
      doneText: "Votre ancienne adresse est de nouveau associée à votre compte. Si vous n'êtes pas à l'origine du changement, pensez à choisir un nouveau mot de passe depuis « Mot de passe oublié ».",
    },
  };
  const wording = mode && titles[mode] ? titles[mode] : titles.resetPassword;

  const renderBody = () => {
    if (status === 'checking') {
      return (
        <div className="flex items-center justify-center gap-3 py-10">
          <CircularProgress size={22} />
          <p className={`${common.body} ${classes.text}`}>Vérification du lien…</p>
        </div>
      );
    }
    if (status === 'invalid') {
      return (
        <div className="flex flex-col items-center text-center gap-4 py-6">
          <LinkOffIcon sx={{ fontSize: 44, color: '#d97706' }} />
          <p className={`font-bold ${classes.text} ${classes.fontFamily}`}>{invalidReason}</p>
          <p className={`${common.body} ${classes.text}`}>
            Demandez un nouveau lien depuis la page de connexion (« Mot de passe oublié ? » ou « Recevoir mon lien de première connexion »).
          </p>
          <ButtonField label="Retour à la connexion" variant="contained" className="font-bold w-full" onClick={() => navigate('/')} />
        </div>
      );
    }
    if (status === 'done') {
      return (
        <div className="flex flex-col items-center text-center gap-4 py-6">
          <CheckCircleIcon sx={{ fontSize: 44, color: '#059669' }} />
          <p className={`font-bold ${classes.text} ${classes.fontFamily}`}>{wording.done}</p>
          <p className={`${common.body} ${classes.text}`}>{wording.doneText}</p>
          <ButtonField label="Se connecter" variant="contained" className="font-bold w-full" onClick={() => navigate('/')} />
        </div>
      );
    }
    return (
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className={`${common.body} ${classes.text}`}>
          Compte : <strong>{email}</strong>. Choisissez le mot de passe que vous utiliserez pour vous connecter.
        </p>
        <FormInput
          label="Nouveau mot de passe"
          name="password"
          type={showPass ? 'text' : 'password'}
          placeholder={`${MIN_PASSWORD_LENGTH} caractères minimum`}
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPassword(e.target.value); setError(''); }}
          showPasswordToggle
          showPassword={showPass}
          onTogglePassword={() => setShowPass(!showPass)}
          required
          autoComplete="new-password"
        />
        <FormInput
          label="Confirmez le mot de passe"
          name="cpassword"
          type={showPass ? 'text' : 'password'}
          placeholder="Le même mot de passe"
          value={cpassword}
          error={error}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setCpassword(e.target.value); setError(''); }}
          required
          autoComplete="new-password"
        />
        <ButtonField
          label="Enregistrer mon mot de passe"
          variant="contained"
          className="font-bold w-full"
          onClick={() => handleSubmit()}
          loading={status === 'working'}
          disabled={status === 'working'}
        />
      </form>
    );
  };

  return (
    <LayoutAuth rightContent={<RightContentLogin />}>
      <div className="w-full lg:w-1/2 h-full flex flex-col justify-center">
        <div className={`max-w-2xl mx-auto w-full bg-white ${isBrandActive(ENV_DECLARONS) ? `${classes.primaryBorder} border` : 'border border-white/80'} rounded-2xl overflow-hidden shadow-[0_24px_48px_-16px_rgba(15,23,42,0.35)] space-y-6`}>
          <div
            className="relative overflow-hidden p-6 md:p-8 flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}F2 60%, ${primary}D9 100%)` }}
          >
            <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <h1 className={`relative ${common.subtitle} text-white font-extrabold mb-2`}>{wording.header}</h1>
            <p className="md:text-base lg:text-lg text-sm relative text-white font-semibold">
              Votre espace {appName}
            </p>
          </div>
          <div className="px-4 md:px-6 pb-6">{renderBody()}</div>
        </div>
      </div>
    </LayoutAuth>
  );
}

export default AuthActionPage;
